import React, {useEffect, useState} from 'react';
import {BaseInputProps} from '../..';

import {Container, Label, StyledMultiSelect} from './styles';
import {useController, useFormContext} from 'react-hook-form';
import {NestedKeys} from 'shared/types/key-manipulation';
import Loading from 'shared/migration-components/loading';
import useApiQuery from 'shared/hooks/use-api-query';
import {useDebounce} from 'use-debounce';
import {SelectDataProps} from 'shared/types/select-data';

export interface AsyncMultiSelectProps<T = any> extends BaseInputProps {
  onClick?: () => void;
  value?: any;
  placeholder?: string;
  gridColumn?: number;
  name: NestedKeys<T>;
  url: (search: string) => string;
  formatData: (data: any, search: string) => SelectDataProps;
  enabledBySearch?: boolean;
}

const AsyncMultiSelect: React.FC<AsyncMultiSelectProps> = ({
  name,
  label,
  onClick,
  placeholder,
  gridColumn,
  border,
  url,
  formatData,
  enabledBySearch = false,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 1000);
  const [values, setValues] = useState<string[]>([]);
  const [defaultOptions, setDefaultOptions] = useState<SelectDataProps[]>([]);

  const [focus, setFocus] = useState(false);
  const {control} = useFormContext();
  const {field} = useController({control, name});

  useEffect(() => {
    const values = (field.value ?? []).map((value: any) =>
      typeof value === 'string' ? value : formatData(value, search).value,
    );

    if (defaultOptions.length === 0) {
      const newDefaultOptions = (field.value ?? [])
        .filter((value: any) => typeof value !== 'string')
        .map((value: any) => formatData(value, search));

      setDefaultOptions(newDefaultOptions);
    }

    if (values.length > 0) setValues(values);
  }, [field.value]);

  const {data, isLoading, remove} = useApiQuery<any[]>({
    url: url(debouncedSearch),
    retry: false,
    enabled: enabledBySearch ? !!debouncedSearch : undefined,
  });

  const multiSelectData = [
    ...(data
      ? Array.isArray(data)
        ? data.map((item) => formatData(item, search))
        : [formatData(data, search)]
      : []),
    ...defaultOptions,
  ];

  const handleOnChange = (values: string[]) => {
    if (enabledBySearch) {
      const multiSelectOptions = multiSelectData.filter((item) =>
        values.includes(item.value),
      );

      setDefaultOptions(multiSelectOptions);
      remove();
    }
    setValues(values);
    field.onChange(values);
  };

  return (
    <Container {...{onClick, gridColumn, focus, border}}>
      <Label>{label}</Label>
      <StyledMultiSelect
        searchable
        {...{placeholder}}
        data={multiSelectData}
        onSearchChange={setSearch}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        value={values}
        onChange={handleOnChange}
        nothingFoundMessage={
          isLoading ? <Loading /> : !data ? undefined : 'Nenhum dado encontrado'
        }
      />
    </Container>
  );
};

export default AsyncMultiSelect;
